import { DestroyRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormGroupDirective } from '@angular/forms';
import { filter, tap } from 'rxjs';

import { FormErrorService } from '@shared/components/form-error-modal/form-error.service';
import { ToastService } from '@shared/components/toast';

import { CourtService } from '@shared/services/court.service';
import { PasswordResetCooldownService } from '../../../services/password-reset-cooldown.service';
import { PasswordResetRequestResourceService } from '../../../services/password-reset-request-resource.service';
import {
  FORGOT_PASSWORD_COURT_ERROR_MODAL,
  FORGOT_PASSWORD_ERROR_MODAL,
  FORGOT_PASSWORD_TOASTS,
} from '../constants/forgot-password.constant';
import { ForgotPasswordFormControls } from '../enums/forgot-password-form-controls.enum';

@Directive({
  selector: '[appForgotPasswordSubmit]',
  host: {
    '(ngSubmit)': 'submitForm()',
  },
})
export class ForgotPasswordSubmitDirective {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formGroup = inject(FormGroupDirective);
  private readonly _resource = inject(PasswordResetRequestResourceService);
  private readonly _cooldown = inject(PasswordResetCooldownService);
  private readonly _toast = inject(ToastService);
  private readonly _errorService = inject(FormErrorService);
  private readonly _courtService = inject(CourtService);

  private _lastHandledRequestId = 0;

  constructor() {
    toObservable(this._resource.value)
      .pipe(
        filter((value) => !!value),
        tap((value) => {
          if (!value) return;
          if (value.requestId <= this._lastHandledRequestId) return;
          this._lastHandledRequestId = value.requestId;

          this._toast.show(FORGOT_PASSWORD_TOASTS.emailSent(value.email));
          this._cooldown.startCooldownMs(60_000);
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();

    toObservable(this._resource.error)
      .pipe(
        filter((error) => !!error),
        tap(() => {
          const currentRequestId = this._resource.currentRequestId();
          if (!currentRequestId) return;
          if (currentRequestId <= this._lastHandledRequestId) return;
          if (this._resource.isLoading()) return;

          this._lastHandledRequestId = currentRequestId;
          this._errorService.displaySimpleErrorModal(
            FORGOT_PASSWORD_ERROR_MODAL.message,
            FORGOT_PASSWORD_ERROR_MODAL.title,
          );
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  protected submitForm() {
    if (this._cooldown.isActive()) return;

    const form = this._formGroup.form;
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const emailControl = form.get(ForgotPasswordFormControls.EMAIL);
    const normalizedEmail = String(emailControl?.value ?? '')
      .toLowerCase()
      .trim();

    if (!normalizedEmail) {
      form.markAllAsTouched();
      return;
    }

    const courtId = Number(
      this._courtService.courtId() || this._courtService.court()?.id,
    );
    if (!courtId) {
      this._errorService.displaySimpleErrorModal(
        FORGOT_PASSWORD_COURT_ERROR_MODAL.message,
        FORGOT_PASSWORD_COURT_ERROR_MODAL.title,
      );
      return;
    }

    this._resource.requestReset({ email: normalizedEmail, courtId });
  }
}
